import React, { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { ref, update, set } from "firebase/database";
import { database, AGORA_APP_ID } from "../../firebaseconfig";
import { useCall } from "../CallContext";
import { FaPhoneSlash } from "react-icons/fa";
import { getAgoraToken, sendPushNotification } from "../../ApiController/ApiService"; // Ensure imports

const appId = AGORA_APP_ID;

const GlobalCallManager = () => {
  const {
    globalUser: user,
    incomingCall,
    setIncomingCall,
    registerCallHandlers,
    globalTeamMembers,
    setIsInCall: setIsInGlobalCall // 🟦 Sync to Context
  } = useCall();

  const client = useRef(null);
  const localAudioTrack = useRef(null);
  const localVideoTrack = useRef(null);

  const [isInCall, setIsInCall] = useState(false);
  const [isVideoCall, setIsVideoCall] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remoteUserJoined, setRemoteUserJoined] = useState(false);
  const [callStatus, setCallStatus] = useState("Connecting...");
  const [currentCaller, setCurrentCaller] = useState(null);
  const [callRoomId, setCallRoomId] = useState(null);

  const isLeaving = useRef(false);

  // Helper to generate consistent room ID
  const getConsistentRoomId = (fromId, toId) => {
    const sortedIds = [fromId, toId].sort();
    return `${sortedIds[0]}_${sortedIds[1]}`;
  };

  // 🔥 INIT AGORA CLIENT ONCE
  useEffect(() => {
    client.current = AgoraRTC.createClient({ mode: "rtc", codec: "h264" });

    client.current.on("user-joined", () => {
      setRemoteUserJoined(true);
      setCallStatus("Call Connected");
    });

    client.current.on("user-published", async (remoteUser, mediaType) => {
      await client.current.subscribe(remoteUser, mediaType);

      if (mediaType === "audio" && remoteUser.audioTrack) {
        remoteUser.audioTrack.play();
      }

      if (mediaType === "video" && remoteUser.videoTrack) {
        // Wait a small delay to ensure DOM is ready
        setTimeout(() => {
          const container = document.getElementById("remote-video");
          if(container) remoteUser.videoTrack.play(container, { fit: "contain" });
        }, 500);
      }
    });

    client.current.on("user-left", leaveCall);

    return () => {
      if (client.current) client.current.removeAllListeners();
    };
  }, []);

  const checkPermissions = async (enableVideo) => {
    try {
      const constraints = enableVideo ? { audio: true, video: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      alert("Camera/Mic permissions required.");
      return false;
    }
  };

  // 🔥 OUTGOING CALL (Triggered from Context)
  const handleOutgoingCall = async (peer, enableVideo) => {
    if (isInCall || !peer || !user) return;
    
    const hasPermission = await checkPermissions(enableVideo);
    if (!hasPermission) return;

    setCurrentCaller(peer);
    setIsVideoCall(enableVideo);
    setIsModalOpen(true);
    setCallStatus("Calling...");

    const roomId = getConsistentRoomId(user._id, peer._id);
    setCallRoomId(roomId);
    const callRef = ref(database, `calls/${roomId}`);

    try {
      const tokenData = await getAgoraToken(user._id);
      await sendPushNotification(peer._id, enableVideo ? "Video" : "Voice", tokenData.lawyerChannel, tokenData.token, { id: user._id, fullName: user.fullName });
      
      await set(callRef, { type: enableVideo ? "video" : "voice", status: "ringing", from: user._id, to: peer._id, channelName: tokenData.lawyerChannel, token: tokenData.token, timestamp: Date.now() });

      await client.current.join(appId, tokenData.lawyerChannel, tokenData.token, null);

      localAudioTrack.current = await AgoraRTC.createMicrophoneAudioTrack();
      await client.current.publish([localAudioTrack.current]);

      if (enableVideo) {
        localVideoTrack.current = await AgoraRTC.createCameraVideoTrack();
        await client.current.publish([localVideoTrack.current]);
        setTimeout(() => {
            const container = document.getElementById("local-video");
            if(container) localVideoTrack.current.play(container, { fit: "contain" });
        }, 500);
      }
      setIsInCall(true);
      setIsInGlobalCall(true); // 🟦 Context sync
      setCallStatus("Ringing...");
    } catch (error) {
      console.error(error);
      leaveCall();
    }
  };

  // 🔥 INCOMING CALL ACCEPT
  const handleAcceptCall = async (callData) => {
    if (!callData) return;
    
    const enableVideo = callData.type === "video";
    const hasPermission = await checkPermissions(enableVideo);
    if (!hasPermission) {
        handleRejectCall(callData);
        return;
    }

    const caller = globalTeamMembers.find((u) => u._id === callData.from);
    setCurrentCaller(caller);
    setIsVideoCall(enableVideo);
    setIsModalOpen(true);
    setCallStatus("Connecting...");
    setCallRoomId(callData.callId);

    const callRef = ref(database, `calls/${callData.callId}`);
    try {
      await update(callRef, { status: "accepted", timestamp: Date.now() });
      await client.current.join(appId, callData.channelName, callData.token, null);

      localAudioTrack.current = await AgoraRTC.createMicrophoneAudioTrack();
      await client.current.publish([localAudioTrack.current]);

      if (enableVideo) {
        localVideoTrack.current = await AgoraRTC.createCameraVideoTrack();
        await client.current.publish([localVideoTrack.current]);
        setTimeout(() => {
            const container = document.getElementById("local-video");
            if(container) localVideoTrack.current.play(container, { fit: "contain" });
        }, 500);
      }

      setIsInCall(true);
      setIsInGlobalCall(true); // 🟦 Context sync
      setIncomingCall(null);
    } catch (err) {
      console.error("Accept error:", err);
      leaveCall();
    }
  };

  // 🔥 REJECT CALL
  const handleRejectCall = async (callData) => {
    if (!callData) return;
    const callRef = ref(database, `calls/${callData.callId}`);
    await update(callRef, { status: "rejected", timestamp: Date.now() });
    setIncomingCall(null);
  };

  // 🔥 LEAVE CALL
  const leaveCall = async () => {
    if (isLeaving.current) return;
    isLeaving.current = true;

    try {
      if (localAudioTrack.current) {
        localAudioTrack.current.stop(); localAudioTrack.current.close(); localAudioTrack.current = null;
      }
      if (localVideoTrack.current) {
        localVideoTrack.current.stop(); localVideoTrack.current.close(); localVideoTrack.current = null;
      }
      if (client.current) await client.current.leave();

      if (callRoomId) {
        const callRef = ref(database, `calls/${callRoomId}`);
        await update(callRef, { status: "ended", timestamp: Date.now() });
      }
    } catch (err) {
      console.error("Leave error:", err);
    } finally {
      setIsInCall(false); 
      setIsInGlobalCall(false); // 🟦 Context sync
      setIsVideoCall(false); 
      setIsModalOpen(false);
      setRemoteUserJoined(false); 
      setCurrentCaller(null); 
      setCallRoomId(null);
      isLeaving.current = false;
    }
  };

  // Register all handlers in Context
  useEffect(() => {
    // Apne CallContext me startOutgoingCall ko pass karne ka provision banayein
    registerCallHandlers(handleAcceptCall, handleRejectCall, handleOutgoingCall);
  }, [globalTeamMembers, user]);

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[9999]">
      <div className="bg-[#1f1a3a] p-6 rounded-xl w-[90%] max-w-2xl text-white text-center shadow-2xl">
        {!isVideoCall && (
            <img src={currentCaller?.image || "https://via.placeholder.com/150"} alt="Caller" className="w-24 h-24 mx-auto rounded-full mb-4 border-4 border-[#4b00c9]" />
        )}
        <h2 className="text-xl font-semibold mb-2">{currentCaller?.fullName || "Unknown Caller"}</h2>
        <p className="text-sm text-gray-300 mb-6">{remoteUserJoined ? "Connected" : callStatus}</p>

        {/* VIDEO MODE UI */}
        {isVideoCall && (
          <div className="flex flex-col md:flex-row gap-4 justify-center mb-6">
            <div id="local-video" className="w-full md:w-64 h-48 bg-black rounded-lg overflow-hidden relative">
                <span className="absolute bottom-2 left-2 text-xs bg-black bg-opacity-50 px-2 py-1 rounded">You</span>
            </div>
            <div id="remote-video" className="w-full md:w-64 h-48 bg-black rounded-lg overflow-hidden relative">
                <span className="absolute bottom-2 left-2 text-xs bg-black bg-opacity-50 px-2 py-1 rounded">{currentCaller?.fullName}</span>
            </div>
          </div>
        )}

        <button onClick={leaveCall} className="p-4 bg-red-600 hover:bg-red-700 transition rounded-full shadow-lg">
          <FaPhoneSlash size={24} />
        </button>
      </div>
    </div>
  );
};

export default GlobalCallManager;